import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import TablePagination from "../../commons/Pagination/TablePagination";
import CopyableId from "../../commons/CopyableId/CopyableId";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";

const STATUS_LABELS = {
  0: "Pending",
  1: "Processing",
  2: "Success",
  3: "Failed",
};

const EMPTY_TRANSACTION = {};

const formatMoney = (value, { signed = false, positive = false } = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const formatted = `$${Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  if (positive && num !== 0) return `+ ${formatted}`;
  if (signed && num !== 0) return `- ${formatted}`;
  return formatted;
};

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatStatus = (status) => {
  if (status === null || status === undefined || status === "") return "N/A";
  if (STATUS_LABELS[status] !== undefined) return STATUS_LABELS[status];
  const text = String(status).replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "processing") return "badge badge-info";
  if (normalized === "pending") return "badge badge-warning";
  if (normalized === "completed" || normalized === "success" || normalized === "paid") {
    return "badge badge-success";
  }
  if (normalized === "failed" || normalized === "rejected") return "badge badge-danger";
  return "badge badge-secondary";
};

const pick = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== "") {
      return source[key];
    }
  }
  return null;
};

const buildTransactionDetail = (transaction = {}, apiDetail = {}) => {
  const safeTransaction =
    transaction && typeof transaction === "object" && !Array.isArray(transaction)
      ? transaction
      : {};
  const safeApiDetail =
    apiDetail && typeof apiDetail === "object" && !Array.isArray(apiDetail)
      ? apiDetail
      : {};
  const merged = { ...safeTransaction, ...safeApiDetail };

  return {
    transactionRefId:
      pick(merged, "transaction_id", "transaction_ref_id", "transactionRefId") || "N/A",
    stripeTransferId: pick(
      merged,
      "transaction_id",
      "stripe_transfer_id",
      "stripeTransferId"
    ),
    grossAmount: pick(merged, "gross_amount", "grossAmount", "total_gross"),
    taxAmount: pick(merged, "taxes", "tax_amount", "taxAmount", "total_tax"),
    commissionAmount: pick(
      merged,
      "platform_commission",
      "platformCommission",
      "commission_amount",
      "commission"
    ),
    debitNotes: pick(merged, "debit_notes", "debit_note", "debit_note_amount", "total_debit_notes"),
    creditNotes: pick(
      merged,
      "credit_notes",
      "credit_note",
      "credit_note_amount",
      "total_credit_notes"
    ),
    netPayable: pick(
      merged,
      "net_paid",
      "net_payable_amount",
      "netPayableAmount",
      "total_net_payable"
    ),
    recipients: pick(merged, "total_order", "recipients", "number_of_order") ?? "N/A",
    initiatedAt: pick(merged, "transaction_date", "createdAt", "created_at", "initiated_at"),
    completedAt: pick(
      merged,
      "transaction_date",
      "payout_at",
      "completed_at",
      "transfer_at",
      "updatedAt"
    ),
    transferAt: pick(merged, "transaction_date", "transfer_at"),
    payoutAt: pick(merged, "transaction_date", "payout_at"),
    status: formatStatus(pick(merged, "status_str", "status")),
  };
};

const mapOrderRow = (item) => {
  const order =
    item?.order_id && typeof item.order_id === "object" ? item.order_id : null;

  const orderNumber =
    pick(item, "order_number", "orderNumber") ||
    pick(order, "order_number", "orderNumber") ||
    (typeof item?.order_id === "string" ? item.order_id : null) ||
    "N/A";

  return {
    id: item?._id || order?._id || orderNumber,
    orderNumber: String(orderNumber),
    orderDate: formatDate(
      pick(item, "order_date", "orderDate", "createdAt", "created_at") ||
        pick(order, "createdAt", "created_at", "order_date")
    ),
    gross: formatMoney(
      pick(item, "gross_amount", "grossAmount", "order_amount", "total_amount") ||
        pick(order, "total_amount", "amount_to_pay", "gross_amount")
    ),
    tax: formatMoney(pick(item, "tax", "tax_amount", "taxAmount")),
    commission: formatMoney(
      pick(item, "commission", "platform_commission", "platformCommission", "commission_amount")
    ),
    debitNotes: formatMoney(pick(item, "debit_notes", "debit_note", "debit_note_amount")),
    creditNotes: formatMoney(pick(item, "credit_notes", "credit_note", "credit_note_amount")),
    netPayable: formatMoney(
      pick(
        item,
        "net_payable",
        "net_payable_amount",
        "netPayableAmount",
        "net_amount",
        "amount_to_pay"
      ) || pick(order, "amount_to_pay", "total_amount")
    ),
    entryType: formatStatus(pick(item, "wallet_entry_type", "entry_type")),
    status: formatStatus(
      pick(item, "status_str", "status", "order_status") ?? pick(order, "order_status", "status")
    ),
  };
};

const parseOrderList = (payload) => {
  const list = payload?.payout_order_detail_list;
  return Array.isArray(list) ? list.map(mapOrderRow) : [];
};

function ScheduledPayoutTransactionDetail() {
  const { payoutId, transactionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const payoutRefId = location.state?.payoutRefId;
  const settlementPeriod = location.state?.settlementPeriod;
  const transactionFromState = useMemo(
    () => location.state?.transaction || EMPTY_TRANSACTION,
    [location.state?.transaction]
  );

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [orders, setOrders] = useState([]);
  const [transactionDetail, setTransactionDetail] = useState(() =>
    buildTransactionDetail(location.state?.transaction || EMPTY_TRANSACTION)
  );
  const [loading, setLoading] = useState(false);
  const limit = 10;

  const transactionFromStateRef = useRef(transactionFromState);
  transactionFromStateRef.current = transactionFromState;

  const fetchOrders = useCallback(async () => {
    const walletPayoutId = transactionId || payoutId;
    if (!walletPayoutId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        wallet_payout_id: walletPayoutId,
      });

      const response = await handleApiCall(
        "get",
        `${endpoints.listOfPayoutOrderDetailList}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        const apiTransaction =
          payload?.wallet_payout &&
          typeof payload.wallet_payout === "object" &&
          !Array.isArray(payload.wallet_payout)
            ? payload.wallet_payout
            : EMPTY_TRANSACTION;

        const orderList = parseOrderList(payload);
        const totalOrders =
          Number(payload?.pagination?.total) || orderList.length || "N/A";

        setTransactionDetail({
          ...buildTransactionDetail(transactionFromStateRef.current, apiTransaction),
          recipients: totalOrders,
        });
        setOrders(orderList);
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              payload?.totalPages ||
              payload?.total_pages ||
              1
          ) || 1
        );
      } else {
        setOrders([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching payout orders:", error);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [transactionId, payoutId, page, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const batchLabel = settlementPeriod
    ? `Batch payout · ${settlementPeriod}`
    : payoutRefId
      ? `Batch payout · ${payoutRefId}`
      : "Batch payout";

  const financialItems = [
    {
      label: "Gross Amount",
      value: formatMoney(transactionDetail.grossAmount),
      className: "",
    },
    {
      label: "Platform Commission",
      value: formatMoney(transactionDetail.commissionAmount, { signed: true }),
      className: "text-danger",
    },
    {
      label: "Taxes",
      value: formatMoney(transactionDetail.taxAmount, { signed: true }),
      className: "text-danger",
    },
    {
      label: "Debit Notes",
      value: formatMoney(transactionDetail.debitNotes, { signed: true }),
      className: "text-danger",
    },
    {
      label: "Credit Notes",
      value: formatMoney(transactionDetail.creditNotes, { positive: true }),
      className: "text-success",
    },
    {
      label: "Net Paid",
      value: formatMoney(transactionDetail.netPayable),
      className: "text-info",
      highlight: true,
    },
  ];

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Payout Transaction</h3>
              <div className="ms-auto">
                <button
                  type="button"
                  className="btn btn-default rounded-lg"
                  onClick={() =>
                    navigate(`/payout-management/scheduled-payouts/${payoutId}`, {
                      state: { payoutRefId, settlementPeriod },
                    })
                  }
                >
                  Back
                </button>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12 mb-4">
                <div className="border rounded-14 p-4 h-100">
                  <div className="d-flex align-items-start justify-content-between flex-wrap mb-3">
                    <div>
                      <div className="d-flex align-items-center flex-wrap mb-2">
                        <h4 className="text-white mb-0 mr-3">
                          <CopyableId value={transactionDetail.transactionRefId} />
                        </h4>
                        <span className={getStatusBadgeClass(transactionDetail.status)}>
                          {transactionDetail.status}
                        </span>
                      </div>
                      <p className="text-muted mb-0">{batchLabel}</p>
                    </div>
                    {transactionDetail.stripeTransferId ? (
                      <a
                        href={`https://dashboard.stripe.com/transfers/${transactionDetail.stripeTransferId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-info"
                      >
                        Stripe Transfer {transactionDetail.stripeTransferId}
                        <span aria-hidden="true"> ↗</span>
                      </a>
                    ) : null}
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-4 mb-3 mb-md-0">
                      <small className="text-muted d-block text-uppercase">Initiated</small>
                      <strong className="text-white">
                        {formatDateTime(transactionDetail.initiatedAt) || "N/A"}
                      </strong>
                    </div>
                    <div className="col-md-4 mb-3 mb-md-0">
                      <small className="text-muted d-block text-uppercase">Completed</small>
                      <strong className="text-white">
                        {formatDateTime(transactionDetail.completedAt) || "N/A"}
                      </strong>
                    </div>
                    <div className="col-md-4">
                      <small className="text-muted d-block text-uppercase">Orders</small>
                      <strong className="text-white">{transactionDetail.recipients}</strong>
                    </div>
                  </div>

                  <h6 className="text-muted text-uppercase mb-3">Financial Summary</h6>
                  <div className="row">
                    {financialItems.map((item) => (
                      <div key={item.label} className="col-md-4 mb-3">
                        <div
                          className={`border rounded-14 p-3 h-100 ${
                            item.highlight ? "border-info" : ""
                          }`}
                        >
                          <small className="text-muted d-block mb-2">{item.label}</small>
                          <strong className={`h5 mb-0 ${item.className || "text-white"}`}>
                            {item.value}
                          </strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 px-0 py-2">
              <div className="heading mb-3">
                <h5 className="text-default weight-400 my-0">Orders</h5>
              </div>
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th>Order Number</th>
                        <th>Order Date</th>
                        <th>Type</th>
                        <th>Gross</th>
                        <th>Tax</th>
                        <th>Commission</th>
                        <th>Debit Notes</th>
                        <th>Credit Notes</th>
                        <th>Net Payable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">
                                {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : orders.length > 0 ? (
                        orders.map((item) => (
                          <tr key={item.id}>
                            <td className="text-left">{item.orderNumber}</td>
                            <td>{item.orderDate}</td>
                            <td>{item.entryType}</td>
                            <td>{item.gross}</td>
                            <td>{item.tax}</td>
                            <td>{item.commission}</td>
                            <td>{item.debitNotes}</td>
                            <td>{item.creditNotes}</td>
                            <td>{item.netPayable}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScheduledPayoutTransactionDetail;